import {useEffect, useState, useCallback, FC} from 'react';
import {getProcessesByPatientId} from '../../api/processes';
import Error from '../common/Error';
import Spinner from '../common/Spinner';
import 'simplebar/dist/simplebar.min.css';
import {PatientProcessesResponse, PatientSurveysResponse} from './types';
import ProcessesElement from './PatientProcessesElement';
import {DEFAULT_ERROR} from '../../api/constants';
import {AlertSettingsResponseProps} from '../settings/alert_alarm/types';
import {getAllAlertSettings} from '../../api/settings/alert_alarm';
import {processAlertType} from '../settings/alert_alarm/constants';
import {useTranslation} from 'react-i18next';
import {getSurveysByPatientId} from '../../api/surveys';

type PatientProcessesProps = {
  id: string
}

const PatientProcesses: FC<PatientProcessesProps> = ({id}) => {
  const {t} = useTranslation('processes');
  const patientId = Number(id);
  const [isLoading, setLoading] = useState(true);
  const [processesData, setProcessesData] =
    useState<Nullable<PatientProcessesResponse>>(null);
  const [processesAlertData, setProcessesAlertData] =
    useState<Nullable<AlertSettingsResponseProps>>(null);
  const [patientSurveysData, setPatientSurveysData] =
    useState<Nullable<PatientSurveysResponse>>(null);
  const [error, setError] = useState('');

  const getAllProcesses = useCallback(async () => {
    setLoading(true);

    try {
      const [
        patientProcessesResponse,
        patientSurveysResponse,
        processesAlertResponse
      ] = await Promise.all([
        getProcessesByPatientId(patientId),
        getSurveysByPatientId(patientId),
        getAllAlertSettings(processAlertType)
      ]);
      setProcessesData(patientProcessesResponse);
      setProcessesAlertData(processesAlertResponse);
      setPatientSurveysData(patientSurveysResponse);
    } catch (error) {
      setError(error?.message || DEFAULT_ERROR);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getAllProcesses();
  }, []);

  if (error) {
    return <Error message={error} />;
  };

  return (
    <>
      {isLoading || !processesData ?
        (<Spinner loadingText={t('loadingText.patientProcesses')} />) :
        (<div className="running-processes-wrapper">
          {!processesData?.length ?
            <div className="empty-table-msg">{t('noAnyRunningProcMsg')}</div> :
            processesData.map((patientProcessItem) => (
              <ProcessesElement
                key={`process_element_${patientProcessItem.id}`}
                patientProcessItem={patientProcessItem}
                processesAlertData={processesAlertData}
                patientSurveysData={patientSurveysData}
              />
            ))}
        </div>)}
    </>
  );
};

export default PatientProcesses;
